import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import { cn } from "@/lib/cn";
import { getProxiedImageUrl } from "@/lib/proxy-url-helper";

interface AvatarSmartProps {
  src?: string;
  alt?: string;
  fallback: React.ReactNode;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  shape?: "rounded" | "square";
  showBorder?: boolean;
}

const sizeClasses = {
  xs: "size-4 text-[10px]",
  sm: "size-6 text-xs",
  md: "size-8 text-sm",
  lg: "size-10 text-base",
  xl: "size-12 text-lg",
};

const shapeClasses = {
  rounded: "rounded-full",
  square: "rounded-md",
};

export const AvatarSmart = React.forwardRef<HTMLDivElement, AvatarSmartProps>(
  ({ src, alt: rawAlt = "", fallback, className, size = "sm", shape = "rounded", showBorder = false }, ref) => {
    const alt = rawAlt.trim();
    const [hasError, setHasError] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const handleLoad = useCallback(() => {
      setIsLoaded(true);
      setHasError(false);
    }, []);

    const handleError = useCallback(() => {
      setHasError(true);
      setIsLoaded(false);
    }, []);

    useEffect(() => {
      if (!src) {
        setIsLoaded(false);
        setHasError(false);
        return;
      }

      setIsLoaded(false);
      setHasError(false);
    }, [src]);

    const shouldShowFallback = !src || hasError || !isLoaded;

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex shrink-0 overflow-hidden",
          sizeClasses[size],
          shapeClasses[shape],
          showBorder && "ring-2 ring-background ring-offset-2",
          className,
        )}
      >
        {src && !hasError && (
          <img
            src={getProxiedImageUrl(src)}
            alt={alt}
            aria-hidden={!alt || undefined}
            className="aspect-square size-full object-cover"
            onLoad={handleLoad}
            onError={handleError}
            style={{
              opacity: isLoaded ? 1 : 0,
              transition: "opacity 0.1s ease-in-out",
            }}
          />
        )}
        {shouldShowFallback && (
          <div
            className={cn(
              "flex size-full items-center justify-center font-semibold text-white",
              shapeClasses[shape],
            )}
            style={{
              opacity: isLoaded ? 0 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            {fallback}
          </div>
        )}
      </div>
    );
  },
);

AvatarSmart.displayName = "AvatarSmart";
